import { useState, useEffect, useRef, useCallback } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import type { User } from "@/lib/supabase";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import { supabase } from "@/lib/supabase";
import { getPageKeyFromPath, navigationSections } from "@/mocks/navigation";
import { UserContext } from "@/context/UserContext";
import type { Employee } from "@/types/employee";

export default function AppShell() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<string>("trial");
  const [userName, setUserName] = useState<string>("使用者");
  const [userEmail, setUserEmail] = useState<string>("");
  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isProfileComplete, setIsProfileComplete] = useState(true);
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [accessiblePages, setAccessiblePages] = useState<string[]>([]);
  const [restrictedPages, setRestrictedPages] = useState<string[]>([]);
  const [hiddenPages, setHiddenPages] = useState<string[]>([]);
  const [authLoading, setAuthLoading] = useState(true);
  const [managerUnreadCount, setManagerUnreadCount] = useState(0);
  const [deptChatUnreadCount, setDeptChatUnreadCount] = useState(0);
  const authCheckedRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const fetchEmployeeInfo = async (email: string | undefined, userId: string | undefined, fallbackName: string) => {
    if (!email) {
      setUserRole("trial");
      setUserName(fallbackName);
      setEmployeeId(null);
      setProfileId(null);
      setEmployee(null);
      setIsProfileComplete(true);
      setAccessiblePages([]);
      setRestrictedPages([]);
      setHiddenPages([]);
      return;
    }

    try {
      // Try to find existing employee record
      const empPromise = supabase
        .from("employees")
        .select("id, role_level, name, email_verified, phone, address, id_card_number, id_card_front_url, id_card_back_url, bank_name, bank_branch, bank_code, bank_account_name, bank_account_number, bank_card_image_url, department, avatar_url, employee_code, created_at")
        .eq("email", email)
        .maybeSingle();

      const timeout = new Promise<null>((_, reject) => setTimeout(() => reject(new Error("timeout")), 5000));
      const empResult = await Promise.race([empPromise, timeout]);
      let emp = empResult && "data" in empResult ? empResult.data : null;

      // Auto-create employee record if not found
      if (!emp && userId) {
        const roleFromMeta = "trial";
        const employeeCode = `EMP-${Date.now()}`;
        const { data: newEmp, error: insertError } = await supabase
          .from("employees")
          .insert({
            user_id: userId,
            name: fallbackName,
            email: email,
            role_level: roleFromMeta,
            status: "active",
            employee_code: employeeCode,
          })
          .select("id, role_level, name, email_verified, phone, address, id_card_number, id_card_front_url, id_card_back_url, bank_name, bank_branch, bank_code, bank_account_name, bank_account_number, bank_card_image_url, department, avatar_url, employee_code, created_at")
          .maybeSingle();

        if (insertError) {
          console.error("[fetchEmployeeInfo] auto-create employee error:", insertError.message);
        } else {
          emp = newEmp;
        }
      }

      const currentRole = emp?.role_level || "trial";
      setUserRole(currentRole);
      setEmployeeId(emp?.id || null);
      setEmployee(emp as Employee | null);
      setIsEmailVerified(!!emp?.email_verified);

      const basicComplete = !!(emp?.name && emp?.phone && emp?.address && emp?.id_card_number);
      const identityComplete = !!(emp?.id_card_front_url && emp?.id_card_back_url);
      const bankComplete = !!(emp?.bank_name && emp?.bank_branch && emp?.bank_code && emp?.bank_account_name && emp?.bank_account_number && emp?.bank_card_image_url);
      const complete = basicComplete && identityComplete && bankComplete;
      setIsProfileComplete(complete || currentRole === "admin");
      setUserName(emp?.name || fallbackName);

      if (userId) {
        try {
          const { data: prof } = await supabase
            .from("profiles")
            .select("id")
            .eq("user_id", userId)
            .maybeSingle();

          if (prof) {
            setProfileId(prof.id);
          } else {
            const { data: newProf, error: createError } = await supabase
              .from("profiles")
              .insert({
                user_id: userId,
                display_name: emp?.name || fallbackName,
                role: emp?.role_level || "employee",
                employee_id: emp?.id || null,
              })
              .select("id")
              .maybeSingle();
            if (!createError && newProf) {
              setProfileId(newProf.id);
            }
          }
        } catch {
          // ignore profile errors
        }
      }

      try {
        const { data: perms } = await supabase
          .from("role_permissions")
          .select("page_key, permission")
          .eq("role_level", currentRole);

        if (perms && perms.length > 0) {
          const accessible = perms.filter((p) => p.permission === "accessible").map((p) => p.page_key);
          const restricted = perms.filter((p) => p.permission === "restricted").map((p) => p.page_key);
          const hidden = perms.filter((p) => p.permission === "hidden").map((p) => p.page_key);
          setAccessiblePages(accessible);
          setRestrictedPages(restricted);
          setHiddenPages(hidden);
        } else {
          // No role_permissions data — let Sidebar fall back to hardcoded roles
          setAccessiblePages([]);
          setRestrictedPages([]);
          setHiddenPages([]);
        }
      } catch {
        setAccessiblePages([]);
        setRestrictedPages([]);
        setHiddenPages([]);
      }
    } catch {
      setUserRole("trial");
      setUserName(fallbackName);
      setEmployeeId(null);
      setEmployee(null);
      setIsProfileComplete(true);
      setAccessiblePages([]);
      setRestrictedPages([]);
      setHiddenPages([]);
    }
  };

  // Restricted/hidden page guard — lightweight, no DB queries
  useEffect(() => {
    if (authLoading || !isAuthenticated) return;
    const currentPageKey = getPageKeyFromPath(location.pathname);
    const isHidden = currentPageKey ? hiddenPages.includes(currentPageKey) : false;
    if (isHidden && location.pathname !== "/") {
      navigate("/", { replace: true });
    }
  }, [authLoading, isAuthenticated, hiddenPages, location.pathname, navigate]);

  // Redirect non-admin with incomplete profile to /profile
  useEffect(() => {
    if (authLoading || !isAuthenticated) return;
    if (userRole === "admin") return;
    if (isProfileComplete) return;
    if (location.pathname === "/profile") return;
    navigate("/profile", { replace: true });
  }, [authLoading, isAuthenticated, userRole, isProfileComplete, location.pathname, navigate]);

  // Auth check — runs ONLY once on mount. Redirects handled by separate effect below.
  useEffect(() => {
    const checkAuth = async () => {
      if (authCheckedRef.current) return;
      authCheckedRef.current = true;

      setAuthLoading(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();

        setIsAuthenticated(!!session);
        setUser(session?.user ?? null);

        const fallbackName =
          (session?.user?.user_metadata?.name as string) ||
          (session?.user?.email?.split("@")[0] || "使用者");

        if (session?.user?.email) {
          setUserEmail(session.user.email);
          setIsEmailVerified(false);

          let emp: any = null;

          // Try to find existing employee record
          const { data: existingEmp, error: empError } = await supabase
            .from("employees")
            .select("id, role_level, name, email_verified, phone, address, id_card_number, id_card_front_url, id_card_back_url, bank_name, bank_branch, bank_code, bank_account_name, bank_account_number, bank_card_image_url, department, avatar_url, employee_code, created_at")
            .eq("email", session.user.email)
            .maybeSingle();

          if (empError) {
            console.error("[checkAuth] employees query error:", empError.message);
          }

          // Auto-create employee record if not found
          if (!existingEmp && session.user.id) {
            const roleFromMeta = (session.user.user_metadata?.role as string) || "trial";
            const employeeCode = `EMP-${Date.now()}`;
            const { data: newEmp, error: insertError } = await supabase
              .from("employees")
              .insert({
                user_id: session.user.id,
                name: fallbackName,
                email: session.user.email,
                role_level: roleFromMeta,
                status: "active",
                employee_code: employeeCode,
              })
              .select("id, role_level, name, email_verified, phone, address, id_card_number, id_card_front_url, id_card_back_url, bank_name, bank_branch, bank_code, bank_account_name, bank_account_number, bank_card_image_url, department, avatar_url, employee_code, created_at")
              .maybeSingle();

            if (insertError) {
              console.error("[checkAuth] auto-create employee error:", insertError.message);
            } else {
              emp = newEmp;
            }
          } else {
            emp = existingEmp;
          }

          const role = emp?.role_level || (session.user.user_metadata?.role as string) || "trial";
          setUserRole(role);
          setEmployeeId(emp?.id || null);
          setEmployee(emp as Employee | null);
          setUserName(emp?.name || fallbackName);
          setIsEmailVerified(!!emp?.email_verified);

          const basicComplete = !!(emp?.name && emp?.phone && emp?.address && emp?.id_card_number);
          const identityComplete = !!(emp?.id_card_front_url && emp?.id_card_back_url);
          const bankComplete = !!(emp?.bank_name && emp?.bank_branch && emp?.bank_code && emp?.bank_account_name && emp?.bank_account_number && emp?.bank_card_image_url);
          const complete = basicComplete && identityComplete && bankComplete;
          setIsProfileComplete(complete || role === "admin");

          const { data: perms, error: permError } = await supabase
            .from("role_permissions")
            .select("page_key, permission")
            .eq("role_level", role);

          if (permError) {
            console.error("[checkAuth] role_permissions query error:", permError.message);
          }

          if (perms && perms.length > 0) {
            const accessible = perms.filter((p) => p.permission === "accessible").map((p) => p.page_key);
            const restricted = perms.filter((p) => p.permission === "restricted").map((p) => p.page_key);
            const hidden = perms.filter((p) => p.permission === "hidden").map((p) => p.page_key);
            setAccessiblePages(accessible);
            setRestrictedPages(restricted);
            setHiddenPages(hidden);
          } else {
            // No role_permissions data — let Sidebar fall back to hardcoded roles
            setAccessiblePages([]);
            setRestrictedPages([]);
            setHiddenPages([]);
          }

          if (session.user.id) {
            supabase
              .from("profiles")
              .select("id")
              .eq("user_id", session.user.id)
              .maybeSingle()
              .then(async (profResult) => {
                if (profResult.data) {
                  setProfileId(profResult.data.id);
                } else {
                  const { data: newProf, error: createProfError } = await supabase
                    .from("profiles")
                    .insert({
                      user_id: session.user.id,
                      display_name: emp?.name || fallbackName,
                      role: emp?.role_level || "employee",
                      employee_id: emp?.id || null,
                    })
                    .select("id")
                    .maybeSingle();
                  if (!createProfError && newProf) {
                    setProfileId(newProf.id);
                  }
                }
              })
              .catch(() => {});
          }
        } else {
          setUserRole("trial");
          setUserName(fallbackName);
          setEmployeeId(null);
          setEmployee(null);
          setIsProfileComplete(true);
          setAccessiblePages([]);
          setRestrictedPages([]);
          setHiddenPages([]);
        }
      } catch {
        setIsAuthenticated(false);
        setUser(null);
        setUserRole("trial");
        setUserName("使用者");
        setUserEmail("");
        setEmployeeId(null);
        setProfileId(null);
        setEmployee(null);
        setIsProfileComplete(true);
        setAccessiblePages([]);
        setRestrictedPages([]);
        setHiddenPages([]);
      } finally {
        setAuthLoading(false);
      }
    };
    checkAuth();
  }, []);

  // Lightweight redirect effect — no DB queries, just checks auth state + path
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated && location.pathname !== "/login") {
      navigate("/login", { replace: true });
    }
    if (isAuthenticated && location.pathname === "/login") {
      navigate("/", { replace: true });
    }
  }, [authLoading, isAuthenticated, location.pathname, navigate]);

  // Auth state change listener
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setIsAuthenticated(!!session);
      setUser(session?.user ?? null);

      const fallbackName =
        (session?.user?.user_metadata?.name as string) ||
        (session?.user?.email?.split("@")[0] || "使用者");

      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED" || event === "USER_UPDATED") {
        if (session?.user?.email) setUserEmail(session.user.email);
        setIsEmailVerified(false);
        fetchEmployeeInfo(session?.user?.email, session?.user?.id, fallbackName);
      }

      if (event === "SIGNED_OUT" || !session) {
        setUserRole("trial");
        setUserName("使用者");
        setUserEmail("");
        setIsEmailVerified(true);
        setEmployeeId(null);
        setProfileId(null);
        setEmployee(null);
        setIsProfileComplete(true);
        setAccessiblePages([]);
        setRestrictedPages([]);
        setHiddenPages([]);
        navigate("/login", { replace: true });
      }
    });

    // Global error interception: catch unhandled auth refresh failures
    const originalOnUnhandledRejection = window.onunhandledrejection;
    window.onunhandledrejection = (evt: PromiseRejectionEvent) => {
      const msg = String(evt.reason?.message || evt.reason || "");
      if (msg.includes("Invalid Refresh Token") || msg.includes("Refresh Token Not Found")) {
        evt.preventDefault();
        setIsAuthenticated(false);
        setUser(null);
        setUserRole("trial");
        setUserName("使用者");
        setUserEmail("");
        setEmployeeId(null);
        setProfileId(null);
        setEmployee(null);
        setIsProfileComplete(true);
        setAccessiblePages([]);
        setRestrictedPages([]);
        setHiddenPages([]);
        navigate("/login", { replace: true });
        return;
      }
      if (originalOnUnhandledRejection) {
        originalOnUnhandledRejection.call(window, evt);
      }
    };

    return () => {
      subscription.unsubscribe();
      window.onunhandledrejection = originalOnUnhandledRejection;
    };
  }, [navigate]);

  // Mobile sidebar close on navigation
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  // Responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Poll manager unread count for sidebar badge
  useEffect(() => {
    const isManagerRole = userRole === "admin" || userRole === "manager";
    if (!isManagerRole || !profileId) return;

    const fetchManagerUnread = async () => {
      try {
        let deptIds: string[] = [];
        if (userRole === "admin") {
          const { data } = await supabase.from("departments").select("id");
          deptIds = (data || []).map((d: any) => d.id);
        } else {
          const { data } = await supabase
            .from("manager_departments")
            .select("department_id")
            .eq("manager_profile_id", profileId);
          deptIds = (data || []).map((d: any) => d.department_id);
        }
        if (deptIds.length === 0) return;

        const { data: unreadData } = await supabase
          .from("department_conversations")
          .select("unread_manager_count")
          .in("department_id", deptIds);

        const total = (unreadData || []).reduce(
          (sum: number, row: any) => sum + (row.unread_manager_count || 0),
          0
        );
        setManagerUnreadCount(total);
      } catch {
        // silent
      }
    };

    fetchManagerUnread();
    const interval = setInterval(fetchManagerUnread, 10000);
    return () => clearInterval(interval);
  }, [userRole, profileId]);

  // Listen for instant unread sync from ManagerCenter page
  useEffect(() => {
    const handleSync = (e: Event) => {
      const total = (e as CustomEvent).detail?.total;
      if (typeof total === "number") {
        setManagerUnreadCount(total);
      }
    };
    window.addEventListener("manager:unread-sync", handleSync);
    return () => window.removeEventListener("manager:unread-sync", handleSync);
  }, []);

  // Poll dept-chat unread count for sidebar badge (employee side)
  useEffect(() => {
    if (!profileId) return;

    const fetchDeptChatUnread = async () => {
      try {
        const { data } = await supabase
          .from("department_conversations")
          .select("unread_employee_count")
          .eq("employee_profile_id", profileId);

        const total = (data || []).reduce(
          (sum: number, row: any) => sum + (row.unread_employee_count || 0),
          0
        );
        setDeptChatUnreadCount(total);
      } catch {
        // silent
      }
    };

    fetchDeptChatUnread();
    const interval = setInterval(fetchDeptChatUnread, 10000);
    return () => clearInterval(interval);
  }, [profileId]);

  // Listen for instant unread sync from DepartmentChat page
  useEffect(() => {
    const handleSync = (e: Event) => {
      const total = (e as CustomEvent).detail?.total;
      if (typeof total === "number") {
        setDeptChatUnreadCount(total);
      }
    };
    window.addEventListener("deptchat:unread-sync", handleSync);
    return () => window.removeEventListener("deptchat:unread-sync", handleSync);
  }, []);

  const refreshEmployee = useCallback(async () => {
    if (!employeeId) return;
    const { data } = await supabase
      .from("employees")
      .select("id, role_level, name, email_verified, phone, address, id_card_number, id_card_front_url, id_card_back_url, bank_name, bank_branch, bank_code, bank_account_name, bank_account_number, bank_card_image_url, department, avatar_url, employee_code, created_at")
      .eq("id", employeeId)
      .maybeSingle();
    if (data) {
      setEmployee(data as Employee);
      setUserName(data.name || userName);
      const basicComplete = !!(data.name && data.phone && data.address && data.id_card_number);
      const identityComplete = !!(data.id_card_front_url && data.id_card_back_url);
      const bankComplete = !!(data.bank_name && data.bank_branch && data.bank_code && data.bank_account_name && data.bank_account_number && data.bank_card_image_url);
      const complete = basicComplete && identityComplete && bankComplete;
      setIsProfileComplete(complete || userRole === "admin");
    }
  }, [employeeId, userName, userRole]);

  const refreshEmailVerified = async () => {
    if (!employeeId) return;
    const { data } = await supabase
      .from("employees")
      .select("email_verified")
      .eq("id", employeeId)
      .maybeSingle();
    if (data?.email_verified) {
      setIsEmailVerified(true);
    }
  };

  const refreshProfileComplete = async () => {
    if (!employeeId) return;
    const { data } = await supabase
      .from("employees")
      .select("name, phone, address, id_card_number, id_card_front_url, id_card_back_url, bank_name, bank_branch, bank_code, bank_account_name, bank_account_number, bank_card_image_url")
      .eq("id", employeeId)
      .maybeSingle();
    if (data) {
      const basicComplete = !!(data.name && data.phone && data.address && data.id_card_number);
      const identityComplete = !!(data.id_card_front_url && data.id_card_back_url);
      const bankComplete = !!(data.bank_name && data.bank_branch && data.bank_code && data.bank_account_name && data.bank_account_number && data.bank_card_image_url);
      const complete = basicComplete && identityComplete && bankComplete;
      setIsProfileComplete(complete || userRole === "admin");
    }
  };

  if (location.pathname === "/login") {
    return <Outlet />;
  }

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <i className="ri-loader-4-line animate-spin text-primary-500 text-3xl" />
          <p className="text-sm text-foreground-500">載入中...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const currentPageKey = getPageKeyFromPath(location.pathname);
  const isRestricted = currentPageKey ? restrictedPages.includes(currentPageKey) : false;
  const isHidden = currentPageKey ? hiddenPages.includes(currentPageKey) : false;

  const currentPageLabel = navigationSections
    .flatMap((s) => s.items)
    .find((item) => item.id === currentPageKey)?.label || (location.pathname === "/haa" ? "酒類訂單產生器" : "儀表板");

  return (
    <UserContext.Provider
      value={{
        userRole,
        userName,
        userEmail,
        employeeId,
        profileId,
        employee,
        isAdmin: userRole === "admin",
        isEmailVerified,
        isProfileComplete,
        refreshEmailVerified,
        refreshProfileComplete,
        refreshEmployee,
      }}
    >
      <div className="flex h-screen bg-background-50 overflow-hidden">
        <aside
          className={`hidden lg:flex flex-col bg-background-50 border-r border-background-200 transition-all duration-300 ${
            sidebarOpen ? "w-64" : "w-16"
          }`}
        >
          <Sidebar
            isCollapsed={!sidebarOpen}
            userRole={userRole}
            accessiblePages={accessiblePages}
            restrictedPages={restrictedPages}
            hiddenPages={hiddenPages}
            onToggle={() => setSidebarOpen(!sidebarOpen)}
            unreadManagerCount={managerUnreadCount}
            unreadDeptChatCount={deptChatUnreadCount}
          />
        </aside>

        {mobileSidebarOpen && (
          <div
            className="fixed inset-0 bg-black/30 z-40 lg:hidden"
            onClick={() => setMobileSidebarOpen(false)}
          />
        )}

        <aside
          className={`fixed inset-y-0 left-0 z-50 w-64 bg-background-50 border-r border-background-200 transform transition-transform duration-300 lg:hidden ${
            mobileSidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <Sidebar isCollapsed={false} userRole={userRole} accessiblePages={accessiblePages} restrictedPages={restrictedPages} hiddenPages={hiddenPages} unreadManagerCount={managerUnreadCount} unreadDeptChatCount={deptChatUnreadCount} />
        </aside>

        <div className="flex-1 flex flex-col min-w-0">
          <Topbar
            onMenuClick={() => setMobileSidebarOpen(true)}
            userName={userName}
            userRole={userRole}
            pageLabel={currentPageLabel}
          />
          <main className="flex-1 overflow-y-auto p-4 md:p-6 relative">
            {isRestricted ? (
              <div className="absolute inset-0 bg-background-50/95 flex items-center justify-center z-10">
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center mx-auto">
                    <i className="ri-lock-line text-accent-600 text-2xl" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-foreground-900">權限不足</h2>
                    <p className="text-sm text-foreground-500 mt-1">您的帳號權限等級無法存取此頁面</p>
                  </div>
                  <button
                    onClick={() => navigate("/")}
                    className="px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer"
                  >
                    返回首頁
                  </button>
                </div>
              </div>
            ) : (
              <Outlet />
            )}
          </main>
        </div>
      </div>
    </UserContext.Provider>
  );
}
